import type { ScheduleEvent, ScheduleEventGameData } from '~/types'
import { Status, StatusLabel } from '~/enums/schedule-event'
import { PeriodType, MAX_PERIODS } from '~/enums/period-type'
import { ordinalSuffix } from '#shared/lib/ordinalSuffix'

export const useGameData = (srIdRef: MaybeRefOrGetter<Maybe<string | undefined>>) => {
  const srId = ref<Maybe<string>>()

  const { getStreamById } = useLiveGamesStore()

  const stream = computed<Maybe<ScheduleEventGameData.Model>>(() => getStreamById(srId.value))

  const hasLiveGameData = computed<boolean>(() => null !== stream.value && null !== stream.value.game)
  const liveGameData = computed<Maybe<ScheduleEventGameData.GameInfo>>(() => hasLiveGameData.value ? stream.value!.game : null)
  const clock = computed<Maybe<string>>(() => hasLiveGameData.value ? liveGameData.value!.clock : null)
  const status = computed<Maybe<ScheduleEvent.Status>>(() => hasLiveGameData.value ? liveGameData.value!.status : null)
  const rawCurrentPeriod = computed<Maybe<number>>(() => hasLiveGameData.value ? liveGameData.value!.quarter : null)
  const currentPeriod = computed<Maybe<number>>(() => {
    if (!rawCurrentPeriod.value) {
      return null
    }

    if (rawCurrentPeriod.value > MAX_PERIODS) {
      return rawCurrentPeriod.value - MAX_PERIODS
    }

    return rawCurrentPeriod.value
  })
  const currentPeriodType = computed<Maybe<PeriodType>>(() => {
    if (!rawCurrentPeriod.value) {
      return null
    }

    if (rawCurrentPeriod.value > MAX_PERIODS) {
      return PeriodType.Overtime
    }

    return PeriodType.Quarter
  })
  const currentPeriodText = computed<Maybe<string>>(() => {
    if (!currentPeriod.value) {
      return null
    }

    if (status.value === Status.Halftime) {
      return StatusLabel.Halftime
    }

    switch (currentPeriodType.value) {
      case PeriodType.Overtime:
        return currentPeriod.value > 1 ? `${currentPeriod.value}OT` : 'OT'
      case PeriodType.Quarter:
        return `${ordinalSuffix(currentPeriod.value)} QTR`
    }

    return null
  })
  const homeTeam = computed<Maybe<ScheduleEventGameData.Team>>(() => hasLiveGameData.value ? liveGameData.value!.summary.home : null)
  const homePoints = computed<Maybe<number>>(() => hasLiveGameData.value ? liveGameData.value!.summary.home.points : null)
  const awayTeam = computed<Maybe<ScheduleEventGameData.Team>>(() => hasLiveGameData.value ? liveGameData.value!.summary.away : null)
  const awayPoints = computed<Maybe<number>>(() => hasLiveGameData.value ? liveGameData.value!.summary.away.points : null)

  watchEffect(() => {
    srId.value = toValue(srIdRef)
  })

  return {
    hasLiveGameData,
    clock,
    status,
    currentPeriod,
    homeTeam,
    homePoints,
    awayTeam,
    awayPoints,
    currentPeriodText,
    currentPeriodType,
  }
}
